import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import {
  COMMUNITY_DEFAULT_AVATAR,
  getStyleObject,
  getStyleObjectFromUrl,
  ProfileService,
  StyleObject,
} from '@outlaw/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { getSocialLinks } from '../../../utils/social-links.util';
import { DialogRef } from '@ngneat/dialog';
import { FollowButtonProfile, HeaderLink, UserProfileDialogData } from '../../../interfaces';
import { CommunityOwnerWithFollow } from '../../../interfaces/owner';
import { FollowButtonComponent } from '../../follow-button/follow-button.component';
import { SocialLinksComponent } from '../../social-links/social-links.component';
import { CommunityLoaderComponent } from '../../community-loader/community-loader.component';
import { DialogWrapperComponent } from '../dialog-wrapper/dialog-wrapper.component';
import { OwnerService } from '../../../services/api/owner.service';
import { FollowerService } from '../../../services/api/followers.service';

@Component({
  selector: 'app-user-profile-dialog',
  templateUrl: './user-profile-dialog.component.html',
  styleUrls: ['./user-profile-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FollowButtonComponent, DialogWrapperComponent, CommunityLoaderComponent, SocialLinksComponent],
})
export class UserProfileDialogComponent implements OnInit {
  public dialogRef = inject<DialogRef<UserProfileDialogData>>(DialogRef);
  public profileService = inject(ProfileService);
  private ownerService = inject(OwnerService);
  private followerService = inject(FollowerService);
  private destroyRef = inject(DestroyRef);
  private cd = inject(ChangeDetectorRef);

  profile: CommunityOwnerWithFollow;
  socialLinks: HeaderLink[];

  followStatusChanged: boolean = false;

  userUUID: string;

  get dialogData(): UserProfileDialogData {
    return this.dialogRef.data;
  }

  get shortProfile(): FollowButtonProfile {
    return {
      passport_uuid: this.profile.passport_uuid,
      is_following: this.profile.is_following,
    };
  }

  constructor() {
    this.observeBackdropClick();
    this.userUUID = this.dialogData.userUUID;
  }

  ngOnInit(): void {
    this.loadOwnerFollowings(this.userUUID);
  }

  getAvatarStyleObject(): StyleObject {
    if (this.profile.avatar_url) {
      return getStyleObject(this.profile, 'avatar_url');
    }

    return getStyleObjectFromUrl(COMMUNITY_DEFAULT_AVATAR);
  }

  onFollowStatusChanged(isFollowing: boolean): void {
    this.followStatusChanged = isFollowing !== this.profile?.is_following;
  }

  private observeBackdropClick(): void {
    this.dialogRef.backdropClick$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.dialogRef.close(this.followStatusChanged);
    });
  }

  private loadOwnerFollowings(userUUID: string) {
    combineLatest([this.ownerService.getOwnerByUUID(userUUID), this.followerService.isFollowing(userUUID)])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([communityOwner, isFollowing]) => {
          this.profile = { ...communityOwner, ...isFollowing };
          this.socialLinks = getSocialLinks(this.profile.social_networks) || [];
          this.cd.detectChanges();
        },
        error: error => console.error('Failed to get community owner', error),
      });
  }
}
